import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import dayjs from "dayjs";

const API_BASE =
  process.env.REACT_APP_API_BASE_URL || "https://upsc-backend-353e.onrender.com";

/** ---------- util helpers ---------- */
const ensureFourOptions = (opts = []) => {
  const arr = [...(opts || [])];
  while (arr.length < 4) arr.push("");
  return arr.slice(0, 4);
};

const trim4 = (opts = []) => ensureFourOptions(opts).map((o) => (o ?? "").trim());

const findNormalizedIndex = (options = [], answer = "") => {
  const normalizedOptions = (options || []).map((o) => (o ?? "").trim());
  const ans = (answer ?? "").trim();
  return normalizedOptions.findIndex((o) => o === ans);
};

/** ---------- main manager ---------- */
const PrelimsTestViewManager = () => {
  const navigate = useNavigate();
  const [tests, setTests] = useState({});
  const [expandedYear, setExpandedYear] = useState(null);
  const [expandedMonth, setExpandedMonth] = useState(null);  

  const getToken = () => JSON.parse(localStorage.getItem("user"))?.token;

  const fetchTests = async () => {
    try {
      const token = getToken();
      const res = await axios.get(`${API_BASE}/api/admin/test-series/prelims`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      // Keep all prelims (draft + published) for admin view
      const prelimsOnly = res.data.filter((test) => test.type === "prelims");

      // group by Year -> Month -> Day
      const grouped = {};
      prelimsOnly.forEach((test) => {
        const date = dayjs(test.date);
        const year = date.format("YYYY");
        const month = date.format("MMMM");
        const day = date.format("DD");

        if (!grouped[year]) grouped[year] = {};
        if (!grouped[year][month]) grouped[year][month] = {};
        if (!grouped[year][month][day]) grouped[year][month][day] = [];

        grouped[year][month][day].push(test);
      });

      setTests(grouped);
    } catch (err) {
      console.error("❌ Failed to fetch tests:", err.response?.data || err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete entire test?")) return;
    try {
      const token = getToken();
      await axios.delete(`${API_BASE}/api/admin/test-series/prelims/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchTests();
    } catch (err) {
      console.error("❌ Delete failed:", err.response?.data || err);
    }
  };

  useEffect(() => {
    fetchTests();
  }, []);

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <h2 className="text-3xl font-bold mb-6 text-gray-900 dark:text-gray-100">
        Manage Prelims Tests
      </h2>

      {Object.keys(tests)
        .sort((a, b) => b - a)
        .map((year) => (
          <div key={year} className="mb-4 border rounded-lg overflow-hidden dark:border-gray-700">
            <div
              className="bg-gray-200 dark:bg-gray-800 px-4 py-2 cursor-pointer font-semibold text-gray-900 dark:text-gray-100 hover:bg-gray-300 dark:hover:bg-gray-700 transition"
              onClick={() => setExpandedYear(expandedYear === year ? null : year)}
            >
              {year}
            </div>

            {expandedYear === year &&
              Object.keys(tests[year])
                .sort((a, b) => new Date(`${b} 1, 2000`) - new Date(`${a} 1, 2000`))
                .map((month) => (
                  <div key={month} className="ml-4 border-l border-gray-300 dark:border-gray-600">
                    <div
                      className="bg-gray-100 dark:bg-gray-900 px-4 py-2 cursor-pointer font-medium text-gray-800 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-800 transition"
                      onClick={() =>
                        setExpandedMonth(
                          expandedMonth === `${year}-${month}` ? null : `${year}-${month}`
                        )
                      }
                    >
                      {month}
                    </div>

                    {expandedMonth === `${year}-${month}` &&
                      Object.keys(tests[year][month])
                        .sort((a, b) => b - a)
                        .map((day) => (
                          <div key={day} className="ml-8 mb-2">
                            <div className="text-sm font-semibold mb-1 text-gray-700 dark:text-gray-300">{`Day ${day}`}</div>
                            <div className="grid gap-3">
                              {tests[year][month][day].map((test) => (
                                <div
                                  key={test._id}
                                  className="p-4 border rounded-lg bg-white dark:bg-gray-800 dark:border-gray-700 shadow hover:shadow-md transition"
                                >
                                  <div className="flex justify-between items-start">
                                    <div>
                                      <div className="font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                                        {test.title}
                                        {test.published ? (
                                          <span className="text-xs px-2 py-0.5 rounded bg-green-100 text-green-700 border border-green-300">
                                            Published
                                          </span>
                                        ) : (
                                          <span className="text-xs px-2 py-0.5 rounded bg-yellow-100 text-yellow-700 border border-yellow-300">
                                            Draft
                                          </span>
                                        )}
                                      </div>
                                      <div className="text-sm text-gray-500 dark:text-gray-400">
                                        {dayjs(test.date).format("DD MMM YYYY")}
                                      </div>
                                      <div className="text-sm mt-1 text-gray-700 dark:text-gray-300">
                                        <span className="font-medium">Questions:</span>{" "}
                                        {test.questions.length}
                                      </div>
                                    </div>
                                    <div className="flex flex-col gap-2">
                                      <button
  onClick={() =>
    navigate(
      `/admin/test-series/prelims/edit/${test._id}`
    )
  }
  className="px-3 py-1 bg-yellow-500 text-white text-sm rounded hover:bg-yellow-600 transition"
>
  Edit
</button>
                                      <button
                                        onClick={() => handleDelete(test._id)}
                                        className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition"
                                      >
                                        Delete
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                  </div>
                ))}
          </div>
        ))}
      
    </div>
  );
};

export default PrelimsTestViewManager;